import { z } from "zod";
import { IsoDateTime, OpaqueId, Position } from "./common.js";
import { PositionType } from "./journey.js";
/**
 * Endpoints d'aide à la décision (CDC §5.3, §5.4, §5.6, §5.7). Tous en `GET`,
 * calcul pur, sans effet de bord. Le backend fait le métier ; le frontend
 * ne fait que dessiner.
 */
// --- Synoptique (§5.4, §12.1) ---------------------------------------------
export const SynopticVehicle = z.object({
    journeyId: OpaqueId,
    vehicleDisplayNumber: z.string(),
    runId: z.string().nullable(),
    runNumber: z.string().nullable(), // n° de sortie nu (« 04 ») ; null ⇒ « ? »
    serviceId: z.string().nullable(),
    deviationSeconds: z.number().int(),
    deviationLabel: z.string(), // pré-formaté serveur : "À l'heure" | "R+MM" | "A-MM"
    positionType: PositionType,
    atTerminus: z.boolean(),
    endOfService: z.boolean(),
    // v1.34 — `true` : sortie prévue au tableau de marche mais **aucun véhicule
    // détecté** (panne, oubli, flux perdu). Le front la grise. `journeyId` est
    // alors synthétique (`absent:<runId>`), `vehicleDisplayNumber` vide.
    absent: z.boolean(),
});
export const SynopticLine = z.object({
    lineId: OpaqueId,
    lineCode: z.string(),
    name: z.string(),
    mode: z.string(),
    onlineCount: z.number().int().nonnegative(),
    withVehicleCount: z.number().int().nonnegative(),
    vehicles: z.array(SynopticVehicle),
});
export const SynopticResponse = z.object({
    at: IsoDateTime,
    lines: z.array(SynopticLine),
});
// --- Thermomètre / image ligne (§5.3, §12.2) ------------------------------
export const ThermometerStop = z.object({
    stopId: OpaqueId,
    name: z.string(),
    sequence: z.number().int(),
    distanceM: z.number().nonnegative(),
});
/**
 * Une **branche** d'un sens (backlog Phase 2 P2-29b). Une ligne en Y — le métro
 * de Rouen : tronc commun Boulingrin→Saint-Sever, puis Georges Braque **ou**
 * Technopôle — a plusieurs séquences d'arrêts pour un même `direction_id`. Chaque
 * branche porte sa séquence complète (tronc + antenne). Une ligne simple a
 * exactement une branche dont `stopIds` == `directions[].stops[].stopId`.
 */
export const ThermometerBranch = z.object({
    branchId: z.string(),
    headsign: z.string(),
    stopIds: z.array(OpaqueId), // séquence ordonnée complète tronc → terminus de la branche
});
export const ThermometerVehicle = z.object({
    journeyId: OpaqueId,
    vehicleDisplayNumber: z.string(),
    direction: z.union([z.literal(0), z.literal(1)]),
    /** Branche desservie (P2-29b). `null` si indéterminable ; sur une ligne simple, l'unique branche du sens. */
    branchId: z.string().nullable(),
    stopIndex: z.number(), // position continue le long de la séquence de sa branche (`branchId`)
    deviationSeconds: z.number().int(),
    positionType: PositionType,
    originHeadsign: z.string(),
    destinationHeadsign: z.string(),
    /**
     * Enrichissements du marqueur (backlog Phase 2 P2-24/25/26/28), depuis
     * `sortie_schedule` (marche de la sortie, tableaux de marche) via
     * `journey.runId`. `null` / `false` si la sortie n'est pas rapprochée
     * (`runId` absent) ou pas dans le jeu PDF.
     */
    nextBattementSeconds: z.number().int().nullable(), // battement au prochain terminus (P2-25)
    endOfService: z.boolean(), // dernière course de la sortie avant la Rentrée dépôt (P2-28, point blanc)
    releve: z.boolean(), // une relève est prévue sur la course en cours (P2-26, contour clignotant)
});
export const ThermometerResponse = z.object({
    lineId: OpaqueId,
    directions: z.array(z.object({
        direction: z.union([z.literal(0), z.literal(1)]),
        /** Sens d'exploitation — `aller` = V1 (voie du bas), `retour` = V2 (voie du haut). */
        role: z.enum(["aller", "retour"]),
        headsign: z.string(),
        /** Séquence « épine » du sens = la branche la plus complète (rétro-compat < v1.24). */
        stops: z.array(ThermometerStop),
        /** Toutes les branches desservies dans ce sens (P2-29b). Toujours ≥ 1. */
        branches: z.array(ThermometerBranch),
    })),
    vehicles: z.array(ThermometerVehicle),
});
// --- Croisement (§5.6) ---------------------------------------------------
export const CrossingResponse = z.object({
    vehicleA: OpaqueId,
    vehicleB: OpaqueId,
    crossing: z.object({
        kind: z.enum(["common-stop", "interpolated", "none"]),
        stopId: OpaqueId.nullable(),
        stopName: z.string().nullable(),
        /** Milieu des deux quais réellement desservis (souvent distincts), pas un quai précis. */
        position: Position.pick({ lat: true, lon: true }).nullable(),
        atApprox: IsoDateTime.nullable(),
        /**
         * `high` = même endroit, même instant ; `medium` = point fiable mais quais
         * écartés (branches d'une `parent_station` large) ou écart temporel notable ;
         * `low` = position interpolée, ou pôle scindé rapproché géographiquement.
         */
        confidence: z.enum(["high", "medium", "low"]).nullable(),
        /**
         * Écart (m) entre les deux quais au point de croisement — non nul quand les
         * deux lignes desservent « le même arrêt » par des quais éloignés (ex. Hôtel
         * de Ville de Rouen). `null` hors `kind: "common-stop"`.
         */
        gapMeters: z.number().nonnegative().nullable(),
    }),
});
// --- Retournement / haut-le-pied (§5.7, §12.7) --------------------------
export const TurnbackOption = z.object({
    stopId: OpaqueId,
    name: z.string(),
    // Toujours `true` : depuis D-016, `options[]` ne contient QUE les arrêts curés
    // (`/admin/**`). Champ conservé pour rétro-compatibilité.
    eligible: z.boolean(),
    noLayover: z.boolean(), // D-016 — demi-tour sans battement (rond-point) déclaré en admin
    inServiceSeconds: z.number().int().nonnegative().nullable(),
    hlpSeconds: z.number().int().nonnegative().nullable(),
    hlpSource: z.enum(["none", "osrm", "external"]),
    gainSeconds: z.number().int().nullable(),
    confidence: z.enum(["high", "low", "unavailable"]),
});
export const TurnbackOptionsResponse = z.object({
    journeyId: OpaqueId,
    computedAt: IsoDateTime,
    // D-016 — uniquement les arrêts de retournement **curés** de la ligne
    // (`/admin/networks/{n}/lines/{id}/turnback-stops`), triés par `gainSeconds`
    // décroissant (le plus profitable en tête). `[]` si rien n'est curé.
    options: z.array(TurnbackOption),
});
// --- Intervalles / bunching, KPI (§5.5) ---------------------------------
export const HeadwaysResponse = z.object({
    lineId: OpaqueId,
    direction: z.union([z.literal(0), z.literal(1)]),
    meanHeadwaySeconds: z.number().nonnegative(),
    maxGapSeconds: z.number().nonnegative(),
    inService: z.number().int().nonnegative(),
    scheduled: z.number().int().nonnegative(),
    targetHeadwaySeconds: z.number().int().positive().nullable(), // D-018 — fréquence cible ; null si < 2 courses
    bunching: z.array(z.object({
        journeyA: OpaqueId,
        journeyB: OpaqueId,
        atStopName: z.string(),
        headwaySeconds: z.number().nonnegative(),
    })),
});
export const KpiResponse = z.object({
    punctualityRatio: z.number().min(0).max(1),
    openAlerts: z.number().int().nonnegative(),
    window: z.enum(["30m", "2h", "day"]),
});
// --- Prochain passage (§12, backlog Phase 2 P2-16) ---------------------
//
// Outil de consultation, PAS d'aide à la décision : aucune exclusion de
// périmètre (une navette, un passage TAE apparaissent normalement). Dérivé
// des courses actives (`live_journeys` → `nextStops`), qui portent déjà
// l'horaire théorique ajusté du temps réel. L'horizon est donc celui des
// courses en circulation ; une course pas encore partie n'y figure pas.
export const NextPassage = z.object({
    journeyId: OpaqueId,
    lineId: OpaqueId,
    lineCode: z.string(),
    direction: z.union([z.literal(0), z.literal(1)]),
    headsign: z.string(),
    vehicleDisplayNumber: z.string().nullable(),
    runId: z.string().nullable(),
    serviceId: z.string().nullable(),
    sequence: z.number().int(), // rang de l'arrêt dans la séquence de la course
    scheduled: IsoDateTime, // heure théorique au point d'arrêt (fuseau de l'arrêt, CDC §3.2)
    expected: IsoDateTime.nullable(), // heure ajustée du temps réel, `null` si aucun temps réel
    etaSeconds: z.number().int(), // (expected ?? scheduled) − now, en secondes ; peut être négatif (imminent)
    deviationSeconds: z.number().int(),
    positionType: PositionType,
    skipped: z.boolean(), // arrêt annoncé supprimé pour cette course (CDC §3.2)
});
export const NextPassagesResponse = z.object({
    stopId: OpaqueId,
    stopName: z.string().nullable(),
    now: IsoDateTime,
    passages: z.array(NextPassage), // triées par `expected ?? scheduled` croissant
});
//# sourceMappingURL=decision.js.map